import { d as debounce } from './debounce-CFN_LA0O.js';
import { t as throttle } from './throttle-CVAVDVkp.js';

/**
 * Positions `.cursor` at a default point (near the related body), and follows the
 * pointer only while it is over the parent `.backdrop`.
 * Matches markup: `<cursor-element class="cursor"></cursor-element>`
 *
 * Inactive overlays (`aria-hidden="true"`) stay dormant — no DOM queries or
 * document listeners — until the related host opens.
 * While the pointer is over the related body, document `mousemove` is unbound so
 * `handleMouseMove` does not run at all.
 *
 * Initial `cursor_position` classes are the animation start; JS parks at the
 * final backdrop spot (24px from the panel edge / top) after open.
 */


class Cursor extends HTMLElement {
  /** Gap between the drawer/panel edge and the cursor, in the visible backdrop. */
  static #DEFAULT_EDGE_GAP = 24;
  /** Parked cursor offset from the top of the backdrop / panel. */
  static #DEFAULT_TOP_OFFSET = 24;
  /** Gap outside a modal’s top-right corner. */
  static #MODAL_EDGE_GAP = 8;
  /** ~1 frame — keeps the custom cursor smooth without querying the DOM every event. */
  static #MOUSE_MOVE_THROTTLE_MS = 16;
  /** Coalesce rapid window resizes while an overlay is open. */
  static #RESIZE_DEBOUNCE_MS = 100;

  /** @type {HTMLElement | null} */
  #dot = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {HTMLElement | null} */
  #relatedBody = null;

  /** Host whose `aria-hidden` gates activation (drawer / modal / mega-menu). */
  /** @type {HTMLElement | null} */
  #visibilityHost = null;

  /** @type {{ left: number; top: number } | null} */
  #defaultPosition = null;

  /** Cached cursor box — avoids layout reads on every follow move. */
  /** @type {{ width: number; height: number } | null} */
  #dotSize = null;

  /** @type {MutationObserver | null} */
  #visibilityObserver = null;

  /** @type {boolean} */
  #listening = false;

  /** @type {boolean} */
  #mousemoveBound = false;

  /** True while the pointer is over the related body (panel / drawer body). */
  #overRelatedBody = false;

  /** True while the cursor is parked at the default (non-following) position. */
  #isAtDefault = false;

  /** @param {string} name */
  #toSelector(name) {
    if (name.startsWith('.') || name.includes('[')) return name;
    return name.includes('__') ? `.${name}` : name;
  }

  /** @returns {string} */
  #getRootSelector() {
    return this.#toSelector(this.dataset.relatedElementClass || 'drawer-element');
  }

  /** @returns {string} */
  #getBodySelector() {
    return this.#toSelector(this.dataset.relatedBodyClass || 'drawer__body');
  }

  /**
   * Related root (drawer/modal/…) or nearest aria-hidden ancestor.
   * @returns {HTMLElement | null}
   */
  #resolveVisibilityHost() {
    return this.closest(this.#getRootSelector()) ?? this.closest('[aria-hidden]');
  }

  /** @returns {boolean} */
  #isHostVisible() {
    if (!this.#visibilityHost) return true;
    return this.#visibilityHost.getAttribute('aria-hidden') !== 'true';
  }

  /** Resolve and cache backdrop + related body. */
  #resolveRelatedElements() {
    this.#backdrop = this.closest('.backdrop');
    const root = this.#visibilityHost ?? this.closest(this.#getRootSelector());
    this.#relatedBody = root?.querySelector(this.#getBodySelector()) ?? null;
  }

  /** Drop inline insets so liquid `cursor_position` classes are the animation start. */
  #clearInlinePosition() {
    if (!this.#dot) return;
    this.#dot.style.left = '';
    this.#dot.style.top = '';
    this.#dot.style.right = '';
  }

  #cacheDotSize() {
    if (!this.#dot) {
      this.#dotSize = null;
      return;
    }

    this.#dotSize = {
      width: this.#dot.offsetWidth,
      height: this.#dot.offsetHeight,
    };
  }

  #refreshDefaultPosition() {
    if (!this.#relatedBody || !this.#dot || !this.#dotSize) {
      this.#defaultPosition = null;
      return;
    }

    const gap = Cursor.#DEFAULT_EDGE_GAP;
    const topOffset = Cursor.#DEFAULT_TOP_OFFSET;
    const { width: cursorWidth, height: cursorHeight } = this.#dotSize;
    const body = this.#relatedBody;

    // Use layout width for side drawers so the open transition doesn't park the
    // cursor against a mid-animation edge from getBoundingClientRect().
    if (body.classList.contains('drawer--right')) {
      const bodyLeft = window.innerWidth - body.offsetWidth;
      this.#defaultPosition = {
        left: bodyLeft - gap - cursorWidth,
        top: topOffset,
      };
      return;
    }

    if (body.classList.contains('drawer--left')) {
      this.#defaultPosition = {
        left: body.offsetWidth + gap,
        top: topOffset,
      };
      return;
    }

    // Modals — park outside the top-right corner (8px above, 8px to the right).
    // Use final centered geometry (not getBoundingClientRect) so the open
    // translate animation doesn't park against a mid-slide edge and jump.
    if (body.classList.contains('modal__body')) {
      const modalGap = Cursor.#MODAL_EDGE_GAP;
      const width = body.offsetWidth;
      const height = body.offsetHeight;
      const top = (window.innerHeight - height) / 2;
      const right = (window.innerWidth + width) / 2;
      this.#defaultPosition = {
        left: right + modalGap,
        top: top - modalGap - cursorHeight,
      };
      return;
    }

    // Other panels — park 24px into the backdrop from the panel edge.
    const { left, top } = body.getBoundingClientRect();
    this.#defaultPosition = {
      left: left - gap - cursorWidth,
      top: top + topOffset,
    };
  }

  /**
   * @param {number} left
   * @param {number} top
   * @param {{ center?: boolean }} [options]
   */
  #setDotPosition(left, top, { center = false } = {}) {
    if (!this.#dot) return;

    if (center && this.#dotSize) {
      left -= this.#dotSize.width / 2;
      top -= this.#dotSize.height / 2;
    }

    // Clear opposing inset utilities (e.g. liquid `right-6`) so left/top can transition.
    this.#dot.style.right = 'auto';
    this.#dot.style.left = `${left}px`;
    this.#dot.style.top = `${top}px`;
  }

  #applyDefaultPosition() {
    if (!this.#defaultPosition) {
      this.#refreshDefaultPosition();
    }
    if (!this.#defaultPosition) return;

    this.#setDotPosition(this.#defaultPosition.left, this.#defaultPosition.top);
    this.#isAtDefault = true;
  }

  #bindMouseMove() {
    if (this.#mousemoveBound || this.#overRelatedBody) return;
    document.addEventListener('mousemove', this.#onMouseMove);
    this.#mousemoveBound = true;
  }

  #unbindMouseMove() {
    if (!this.#mousemoveBound) return;
    document.removeEventListener('mousemove', this.#onMouseMove);
    this.#onMouseMove.cancel?.();
    this.#mousemoveBound = false;
  }

  #onRelatedBodyEnter = () => {
    this.#overRelatedBody = true;
    this.#unbindMouseMove();
    this.#refreshDefaultPosition();
    this.#applyDefaultPosition();
  };

  #onRelatedBodyLeave = () => {
    this.#overRelatedBody = false;
    this.#bindMouseMove();
  };

  /**
   * Re-park after the panel finishes opening. Only `translate` — opacity/visibility
   * also transition and would otherwise re-run park several times per open.
   * @param {TransitionEvent} event
   */
  #onRelatedBodyTransitionEnd = (event) => {
    if (event.target !== this.#relatedBody || !this.#isAtDefault) return;
    if (event.propertyName !== 'translate') return;
    this.#refreshDefaultPosition();
    this.#applyDefaultPosition();
  };

  /** @param {MouseEvent} event */
  #isPointerOverBackdrop(event) {
    if (!this.#backdrop) return false;

    const target = event.target;
    return target instanceof Node && this.#backdrop.contains(target);
  }

  /** @param {MouseEvent} event */
  #handleMouseMove(event) {
    if (!this.#dot || this.#overRelatedBody) return;

    if (!this.#isPointerOverBackdrop(event)) {
      if (!this.#isAtDefault) {
        this.#refreshDefaultPosition();
        this.#applyDefaultPosition();
      }
      return;
    }

    this.#isAtDefault = false;
    this.#setDotPosition(event.clientX, event.clientY, { center: true });
  }

  #onMouseMove = throttle((event) => {
    this.#handleMouseMove(event);
  }, Cursor.#MOUSE_MOVE_THROTTLE_MS);

  #onResize = debounce(() => {
    this.#cacheDotSize();
    this.#refreshDefaultPosition();
    this.#applyDefaultPosition();
  }, Cursor.#RESIZE_DEBOUNCE_MS);

  #activate() {
    if (this.#listening) return;

    this.#resolveRelatedElements();

    // Start from liquid `cursor_position`, then park so the CSS transition runs.
    this.#clearInlinePosition();
    this.#cacheDotSize();

    this.#refreshDefaultPosition();
    this.#applyDefaultPosition();

    this.#relatedBody?.addEventListener('pointerenter', this.#onRelatedBodyEnter);
    this.#relatedBody?.addEventListener('pointerleave', this.#onRelatedBodyLeave);
    this.#relatedBody?.addEventListener('transitionend', this.#onRelatedBodyTransitionEnd);

    // Opened under an already-hovering pointer — park and skip mousemove until leave.
    if (this.#relatedBody?.matches(':hover')) {
      this.#onRelatedBodyEnter();
    } else {
      this.#bindMouseMove();
    }

    window.addEventListener('resize', this.#onResize);
    this.#listening = true;
  }

  #deactivate() {
    if (!this.#listening) return;

    this.#relatedBody?.removeEventListener('pointerenter', this.#onRelatedBodyEnter);
    this.#relatedBody?.removeEventListener('pointerleave', this.#onRelatedBodyLeave);
    this.#relatedBody?.removeEventListener('transitionend', this.#onRelatedBodyTransitionEnd);
    this.#unbindMouseMove();
    window.removeEventListener('resize', this.#onResize);
    this.#onResize.cancel?.();

    // Keep the current park/follow position while the backdrop fades out.
    // Inline styles are cleared on the next `#activate` so open can animate
    // from liquid `cursor_position` again.

    this.#backdrop = null;
    this.#relatedBody = null;
    this.#defaultPosition = null;
    this.#dotSize = null;
    this.#overRelatedBody = false;
    this.#isAtDefault = false;
    this.#listening = false;
  }

  #syncActive() {
    if (this.#isHostVisible()) {
      this.#activate();
    } else {
      this.#deactivate();
    }
  }

  connectedCallback() {
    this.#dot = this.classList.contains('cursor') ? this : this.querySelector('.cursor');
    if (!this.#dot) return;

    this.#visibilityHost = this.#resolveVisibilityHost();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new MutationObserver(() => this.#syncActive());

    if (this.#visibilityHost) {
      this.#visibilityObserver.observe(this.#visibilityHost, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
      });
    }

    this.#syncActive();
  }

  disconnectedCallback() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#visibilityHost = null;
    this.#deactivate();
  }
}

customElements.define('cursor-element', Cursor);
